import React from "react";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Home, Layout, Sofa, Lightbulb } from "lucide-react";

const services = [
  {
    id: "end-to-end",
    icon: Home,
    title: "End-to-End Interiors",
    description:
      "Complete home transformation from concept to installation with dedicated project management.",
    image: "https://images.pexels.com/photos/1080721/pexels-photo-1080721.jpeg?w=800&q=80",
  },
  {
    id: "modular",
    icon: Layout,
    title: "Modular Interiors",
    description:
      "Smart, space-saving modular solutions for kitchens, wardrobes, and living spaces.",
    image: "https://images.unsplash.com/photo-1696986681606-b156ccd761c5?w=800&q=80",
  },
  {
    id: "complete-home",
    icon: Sofa,
    title: "Complete Home Interiors",
    description:
      "Holistic design approach covering every room with coordinated aesthetics and functionality.",
    image: "https://images.unsplash.com/photo-1628745277862-bc0b2d68c50c?w=800&q=80",
  },
  {
    id: "decor-appliances",
    icon: Lightbulb,
    title: "Furniture & Decor",
    description:
      "Premium furniture, decor pieces, lighting, and appliance integration services.",
    image: "https://images.unsplash.com/photo-1650532897813-d92427f7d229?w=800&q=80",
  },
];

export const ServicesSection = () => {
  return (
    <section id="services" className="section bg-background">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="uppercase tracking-[0.3em] text-[11px] text-muted-foreground mb-4">
            Our Services
          </p>
          <h2 className="font-display text-4xl md:text-5xl font-semibold text-foreground">
            End-to-End Luxury Interiors
          </h2>
          <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
            Experience the ease and efficiency of comprehensive interior solutions,
            where our expert team handles everything from concept to completion.
          </p>
        </motion.div>

        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          data-testid="services-grid"
        >
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card
                  className="group relative overflow-hidden border-[hsl(var(--border))] hover:shadow-[0_24px_60px_rgb(0_0_0/0.2)] transition-all duration-500"
                  data-testid={`service-card-${service.id}`}
                >
                  <div className="aspect-[4/3] relative overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--brand-onyx))] via-transparent to-transparent opacity-60"></div>
                  </div>
                  <CardContent className="p-6 relative">
                    <div className="w-12 h-12 rounded-full bg-[hsl(var(--brand-gold))]/10 flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-[hsl(var(--brand-gold))]" strokeWidth={1.5} />
                    </div>
                    <h3 className="font-serif-alt text-xl font-semibold mb-2">
                      {service.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {service.description}
                    </p>
                  </CardContent>
                  {/* Hover Effect */}
                  <div className="absolute inset-0 bg-[radial-gradient(120%_120%_at_80%_-10%,hsl(var(--brand-champagne))_0%,transparent_60%)] opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
